import moment from "moment";
import { Component } from "vue-property-decorator";
import { VueBase } from "../../Controls/VueBase";
import { mapState } from "vuex";
import { JourFerieDto } from "../../../dto/GenApi.iNotPaie.Dto/JourFerieDto";
import { ServicesFactoryInstance } from "../../Controls/ServicesFactory";
import { NotificationServiceInstance } from "../../../shared/services/notification.service";
import { AbsencesPlanningMensuelDto } from "../../../dto/GenApi.iNotPaie.Dto/AbsencesPlanningMensuelDto";
import dateHelper from "../../../Helper/dateHelper";
import { EventBus } from "../../Controls/EventBus";
import EventConstants from "../../Controls/EventConstants";
import { CalendarDay } from "../../../shared/interfaces/calendar.interface";

@Component({
    components: {
        NavigationMois: require("./navigationMois/navigationMois.vue.html"),
        AbsencePlanningMensuel: require("./absencePlanningMensuel/absencePlanningMensuel.vue.html")
    },
    computed: {
        ...mapState("ParametersStore", ["storeVariableListeJoursFeries"])
    }
})
export default class CalendrierMensuel extends VueBase {
    public listAbsences: AbsencesPlanningMensuelDto[] = [];
    public currentMonth = moment().month();
    public currentYear = moment().year();
    public isAbsencesLoaded = false;

    public storeVariableListeJoursFeries!: JourFerieDto[]; // Mappé depuis le store Vuex

    public async mounted(): Promise<void> {
        EventBus.$on(EventConstants.UPDATE_CONGES, () => this.setAbsences());

        await this.$store.dispatch("ParametersStore/initializeJoursFeries");
        await this.setAbsences();
    }

    /**
     * Méthode pour récupérer les absences de l'utilisateur connecté
     * @returns {Promise<void>}
     */
    private async setAbsences(): Promise<void> {
        this.isAbsencesLoaded = false;
        try {
            const calendarDaysMap = this.calendarDays.map((day) => day.date);
            const dateDebut = moment(calendarDaysMap[0]).utcOffset(0, true).toDate();
            const dateFin = moment(calendarDaysMap[calendarDaysMap.length - 1]).utcOffset(0, true).toDate();

            this.listAbsences = await ServicesFactoryInstance.AbsencesService.getAbsencesPlanningMensuel(dateHelper.formatDateForUrl(dateDebut), dateHelper.formatDateForUrl(dateFin));
        } catch {
            NotificationServiceInstance.SendFailed("Une erreur est survenue lors de la récupération des absences.");
        } finally {
            this.isAbsencesLoaded = true;
        }
    }

    /**
     * Retourne les légendes des absences
     * @returns Array des légendes des absences
     */
    public get calendarLegends(): { libelle: string, color: string }[] {
        const legendMap = new Map<string, { libelle: string, color: string }>();

        for (const absence of this.listAbsences) {
            const code = absence.CodeTypeAbsence as string;
            if (!legendMap.has(code) && absence.LibelleTypeAbsence) {
                legendMap.set(code, {
                    libelle: absence.LibelleTypeAbsence as string,
                    color: absence.Color as string
                });
            }
        }

        return Array.from(legendMap.values());
    }

    /**
     * Retourne les jours de la semaine en version courte, en commençant par lundi
     * @returns Array des trois premières lettres des jours de la semaine en majuscules
     */
    public get weekdaysShort(): string[] {
        const days = moment.weekdaysShort();
        // On réorganise pour commencer par lundi
        return [...days.slice(1), days[0]];
    }

    /**
     * Retourne un objet Moment représentant le premier jour du mois courant
     * @returns Moment du premier jour du mois affiché
     */
    public get currentMoment(): moment.Moment {
        return moment([this.currentYear, this.currentMonth, 1]);
    }

    /**
     * Retourne le nombre de jours dans le mois actuellement affiché
     * @returns Nombre de jours dans le mois
     */
    public get daysInMonth(): number {
        return this.currentMoment.daysInMonth();
    }

    /**
     * Retourne le jour de la semaine du premier jour du mois (1=Lundi, 7=Dimanche)
     * Utilise isoWeekday() pour avoir une semaine qui commence le lundi
     * @returns Index du jour de la semaine selon ISO (1-7)
     */
    public get firstDayOfMonth(): number {
        // isoWeekday retourne 1 pour lundi, 7 pour dimanche
        // On soustrait 1 pour avoir 0 pour lundi, 6 pour dimanche (pour le calcul du décalage)
        return this.currentMoment.isoWeekday() - 1;
    }

    /**
     * Génère tous les jours à afficher dans le calendrier (42 jours = 6 semaines)
     * Inclut les jours du mois précédent, du mois courant et du mois suivant
     * @returns Array de 42 jours avec leurs informations
     */
    public get calendarDays(): CalendarDay[] {
        const firstDay = this.firstDayOfMonth;
        const daysInMonth = this.daysInMonth;

        // Jours du mois précédent
        const previousMonthDays = Array.from({ length: firstDay }, (_, i): CalendarDay => {
            const date = moment(this.currentMoment).subtract(firstDay - i, 'days').toDate();
            return {
                day: date.getDate(),
                isCurrentMonth: false,
                isCurrentDay: dateHelper.isCurrentDay(date, this.currentMonth, this.currentYear),
                isWeekEndDay: dateHelper.isWeekend(date),
                isNonWorkingDay: dateHelper.isNonWorkingDay(date, this.storeVariableListeJoursFeries),
                date
            };
        });

        // Jours du mois actuel
        const currentMonthDays = Array.from({ length: daysInMonth }, (_, i): CalendarDay => {
            const date = moment(this.currentMoment).date(i + 1).toDate();
            return {
                day: i + 1,
                isCurrentMonth: true,
                isCurrentDay: dateHelper.isCurrentDay(date, this.currentMonth, this.currentYear),
                isWeekEndDay: dateHelper.isWeekend(date),
                isNonWorkingDay: dateHelper.isNonWorkingDay(date, this.storeVariableListeJoursFeries),
                date
            };
        });

        // Jours du mois suivant pour compléter la grille (42 jours = 6 semaines)
        const totalDays = previousMonthDays.length + currentMonthDays.length;
        const nextMonthDays = Array.from({ length: 42 - totalDays }, (_, i): CalendarDay => {
            const date = moment(this.currentMoment).add(1, 'month').date(i + 1).toDate();
            return {
                day: i + 1,
                isCurrentMonth: false,
                isCurrentDay: dateHelper.isCurrentDay(date, this.currentMonth, this.currentYear),
                isWeekEndDay: dateHelper.isWeekend(date),
                isNonWorkingDay: dateHelper.isNonWorkingDay(date, this.storeVariableListeJoursFeries),
                date
            };
        });

        return [...previousMonthDays, ...currentMonthDays, ...nextMonthDays];
    }

    /**
     * Retourne les numéros des 6 semaines du calendrier
     * @returns Array [1, 2, 3, 4, 5, 6]
     */
    public get weekNumbers(): number[] {
        return [1, 2, 3, 4, 5, 6];
    }

    /**
     * Met à jour le mois et l'année actuelle et charge les absences
     * @param data Objet contenant le mois et l'année
     * @returns Promise<void>
     */
    public async updateMonth(data: { month: number, year: number }): Promise<void> {
        this.currentMonth = data.month;
        this.currentYear = data.year;
        await this.setAbsences();
    }

    /**
     * Retourne les 7 jours d'une semaine spécifique
     * @param weekNumber Numéro de la semaine (1-5)
     * @returns Array de 7 jours
     */
    public getWeekDays(weekNumber: number): CalendarDay[] {
        const startIndex = (weekNumber - 1) * 7;
        return this.calendarDays.slice(startIndex, startIndex + 7);
    }

    /**
     * Construit une map indiquant la première semaine où chaque absence apparaît
     * Utilisé pour déterminer où afficher le texte de l'absence
     * @returns Map<absenceId, weekNumber>
     */
    public get firstWeekMap(): Map<number, number> {
        const firstWeekMap = new Map<number, number>();

        for (let week = 1; week <= 6; week++) {
            const days = this.getWeekDays(week);
            const wStart = moment(days[0].date).startOf('day');
            const wEnd = moment(days[6].date).endOf('day');

            this.listAbsences.forEach((absence) => {
                if (firstWeekMap.has(absence.Id)) return;

                const absenceStart = moment(absence.DateDebut);
                const absenceEnd = moment(absence.DateFin);
                
                // Si l'absence chevauche cette semaine, c'est sa première apparition
                if (absenceStart.isSameOrBefore(wEnd) && absenceEnd.isSameOrAfter(wStart)) {
                    firstWeekMap.set(absence.Id, week);
                }
            });
        }

        return firstWeekMap;
    }

    /**
     * Retourne les absences qui traversent une semaine spécifique
     * Simple filtrage - la logique de positionnement est dans le composant AbsencePlanningMensuel
     * @param weekNumber Numéro de la semaine (1-6)
     * @returns Array des absences de cette semaine
     */
    public getAbsencesForWeek(weekNumber: number): AbsencesPlanningMensuelDto[] {
        const weekDays = this.getWeekDays(weekNumber);
        const weekStart = moment(weekDays[0].date).startOf('day');
        const weekEnd = moment(weekDays[6].date).endOf('day');

        return this.listAbsences.filter((absence) => {
            const absenceStart = moment(absence.DateDebut);
            const absenceEnd = moment(absence.DateFin);
            return absenceStart.isSameOrBefore(weekEnd) && absenceEnd.isSameOrAfter(weekStart);
        });
    }
}



// WEBPACK FOOTER //
// ./src/components/gestionDesAbsencesV2/calendrierMensuel/calendrierMensuel.ts